import {
  FormControl,
  TextField,
  Autocomplete,
  FilterOptionsState,
  AutocompleteChangeReason,
  AutocompleteChangeDetails
} from "@material-ui/core";
import { TeamMember } from "../../../@types/custom";
import * as React from "react";
import { useTranslation } from "react-i18next";
import useFirestore from "hooks/useFirestore";
import { AutocompleteTag } from "./AutocompleteTag";
import { AutocompleteOption } from "./AutocompleteOption";

type MembersListProps = {
  members: string[];
  myTeamData: TeamMember[];
  selectedMembers: string[];
  handleMemberClick(value: string[]): void;
  /** Virtual number → customer-defined tag naming the lead source it's used for. */
  tagsByNumber?: Record<string, string>;
};

export function MembersList(props: MembersListProps): React.ReactElement {
  const { members, myTeamData, selectedMembers, handleMemberClick, tagsByNumber = {} } = props;

  console.log({ members, myTeamData, selectedMembers });

  const { userSettings } = useFirestore();
  const { t } = useTranslation();
  const myTeam = [...myTeamData, userSettings as TeamMember];

  function handleChange(
    event: React.SyntheticEvent<Element, Event>,
    value: string[],
    reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails
  ) {
    const isClear = reason === "clear";
    const isToggleAll = !/\d/g.test(details?.option) || !/@/g.test(details?.option);

    if (isClear) {
      handleMemberClick(value);
      return;
    }

    handleMemberClick(isToggleAll ? ["toggle", ...value] : value);
  }

  function handleAutocompleteFilter(options: string[], state: FilterOptionsState<string>) {
    return options.filter((opt) => {
      const [email, number] = opt.split("|");

      // means it is "Toggle all" checkbox - it should always be excluded from search results
      if (!email.includes("@") && state.inputValue.length > 0) {
        return false;
      }

      const { ownerFullName, ownerPhoneNumber } = myTeam.find((tm) => tm?.email === email) ?? {};
      const currentValue = state.inputValue.toLocaleLowerCase();

      const isMatch =
        ownerFullName?.toLocaleLowerCase().includes(currentValue) ||
        email.includes(currentValue) ||
        ownerPhoneNumber?.toString().includes(currentValue) ||
        // Searching by the number's tag ("yad2", "billboard", ...) is the whole
        // point of tagging them, so match on it too.
        tagsByNumber[number]?.toLocaleLowerCase().includes(currentValue) ||
        opt.includes(currentValue);

      return isMatch;
    });
  }

  return (
    <FormControl sx={{ maxWidth: "400px" }} fullWidth>
      <Autocomplete
        multiple
        autoHighlight
        disableCloseOnSelect
        value={selectedMembers}
        id="tags-outlined"
        options={[t("callsPage.toggleMembers"), ...members]}
        onChange={handleChange}
        filterOptions={handleAutocompleteFilter}
        renderTags={(tags) => {
          if (members.length === selectedMembers.length) {
            return <AutocompleteTag>{t("general.allSelected")}</AutocompleteTag>;
          }
          return tags.map((member) => (
            <AutocompleteTag
              key={member}
              member={member}
              myTeam={myTeam}
              tagsByNumber={tagsByNumber}
            />
          ));
        }}
        renderOption={(props, member, state) => {
          const index = props["data-option-index"];
          return (
            <AutocompleteOption
              index={index}
              selectedMembers={selectedMembers}
              member={member}
              members={members}
              myTeam={myTeam}
              tagsByNumber={tagsByNumber}
              isSearchActive={state.inputValue.length > 0}
              {...props}
            />
          );
        }}
        renderInput={(params) => (
          <TextField {...params} label={t("callsPage.filterByTeamMember")} />
        )}
      />
    </FormControl>
  );
}
